import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { RTUData } from '../../types/RtuData';
import RTUInfo from './RTUInfo';
import SystemParameters from './SystemParameters';
import SystemEvents from './SystemEvents';
import RTUDiagnosticsTabbedTree from './RTUDiagnosticsTabbedTree';

import { RtuServerPortsCard } from './RtuServerPortsCard';
import { RtuFirewallCard } from './RtuFirewallCard';
import { RtuPortForwardCard } from './RtuPortForwardCard';
import { RtuVirtualIpsCard } from './RtuVirtualIpsCard';
import { createModuleHealthStatusBodyTemplate } from './moduleHealthStatusTemplate';

const chunkArray = (arr: any[], size: number): any[][] =>
  arr.length ? [arr.slice(0, size), ...chunkArray(arr.slice(size), size)] : [];

/** PrimeFlex `md` breakpoint — side-by-side static layout */
const LEGACY_V2_STATIC_SIDE_BY_SIDE_MQ = '(min-width: 768px)';

const LegacyV2StaticDataSection = ({ data }: { data: RTUData | null | undefined }) => {
  const staticData = data?.rtuStaticData;
  const modulesRef = useRef<HTMLDivElement>(null);
  const [infoColumnHeightPx, setInfoColumnHeightPx] = useState<number | undefined>(undefined);

  const syncInfoHeightToModules = useCallback(() => {
    const el = modulesRef.current;
    if (!el || typeof window === 'undefined') {
      return;
    }
    if (!window.matchMedia(LEGACY_V2_STATIC_SIDE_BY_SIDE_MQ).matches) {
      setInfoColumnHeightPx(undefined);
      return;
    }
    const h = el.getBoundingClientRect().height;
    setInfoColumnHeightPx(h > 0 ? Math.round(h) : undefined);
  }, []);

  useLayoutEffect(() => {
    const el = modulesRef.current;
    if (!el || typeof window === 'undefined') {
      return undefined;
    }

    const mq = window.matchMedia(LEGACY_V2_STATIC_SIDE_BY_SIDE_MQ);
    const onMq = () => syncInfoHeightToModules();
    mq.addEventListener('change', onMq);

    const ro = new ResizeObserver(() => syncInfoHeightToModules());
    ro.observe(el);
    syncInfoHeightToModules();

    return () => {
      mq.removeEventListener('change', onMq);
      ro.disconnect();
    };
  }, [data, syncInfoHeightToModules]);

  return (
    <section className="grid rtu-stable-layout rtu-static-data-section align-items-start">
      <div
        className="col-12 md:col-4 rtu-static-data-section__info"
        style={
          infoColumnHeightPx != null
            ? {
                height: infoColumnHeightPx,
                minHeight: infoColumnHeightPx,
                maxHeight: infoColumnHeightPx,
              }
            : undefined
        }
      >
        <RTUInfo
          rtuStaticData={staticData}
          rtuHeader={data?.rtuHeader}
          stripCompositeRedundancies
        />
      </div>
      <div ref={modulesRef} className="col-12 md:col-8 rtu-static-data-section__modules">
        <div className="rtu-static-data-modules">
          <RtuServerPortsCard data={data} />
          <RtuFirewallCard data={data} />
          <RtuPortForwardCard data={data} />
          <RtuVirtualIpsCard data={data} />
        </div>
      </div>
    </section>
  );
};

export const LegacyV2RtuDetailView = ({ data, modules }: { data: RTUData | null | undefined; modules: any[] }) => {
  const { t } = useTranslation();
  const moduleStatusBodyTemplate = useMemo(() => createModuleHealthStatusBodyTemplate(t), [t]);
  const chunkSize = Math.ceil(modules.length / 4) || 1;
  const chunkedData = chunkArray(modules, chunkSize);

  return (
    <>
      <LegacyV2StaticDataSection data={data} />

      <section className="grid rtu-stable-layout">
        <div className="col-12 md:col-6" style={{ padding: '0.25rem' }}>
          <SystemParameters systemData={data?.systemData} />
        </div>
        <div className="col-12 md:col-6" style={{ padding: '0.25rem' }}>
          <SystemEvents systemEvents={data?.systemEvents} />
        </div>
      </section>

      <section className="grid rtu-stable-layout">
        <div className="col-12" style={{ padding: '0.25rem' }}>
          <Card title={t('rtuDetail.sections.modules')} className="compact-card flat-card modules-card">
            <div className="grid" style={{ margin: 0 }}>
              {chunkedData.map((chunk: any[], index: number) => (
                <div key={index} className="col-12 md:col-6 lg:col-3" style={{ padding: '0.25rem' }}>
                  <DataTable
                    value={chunk}
                    className="p-datatable-sm"
                    showGridlines
                    size="small"
                    style={{ fontSize: '0.8rem' }}
                    tableStyle={{ tableLayout: 'auto', width: '100%' }}
                  >
                    <Column field="module" header={t('rtuDetail.modules.module')} style={{ whiteSpace: 'nowrap' }} />
                    <Column
                      body={moduleStatusBodyTemplate}
                      field="active"
                      header={t('rtuDetail.modules.active')}
                      bodyStyle={{ textAlign: 'center' }}
                      headerStyle={{ textAlign: 'center' }}
                      style={{ width: '1%', whiteSpace: 'nowrap' }}
                    />
                    <Column
                      body={moduleStatusBodyTemplate}
                      field="license"
                      header={t('rtuDetail.modules.license')}
                      bodyStyle={{ textAlign: 'center' }}
                      headerStyle={{ textAlign: 'center' }}
                      style={{ width: '1%', whiteSpace: 'nowrap' }}
                    />
                    <Column
                      body={moduleStatusBodyTemplate}
                      field="status"
                      header={t('rtuDetail.modules.status')}
                      bodyStyle={{ textAlign: 'center' }}
                      headerStyle={{ textAlign: 'center' }}
                      style={{ width: '1%', whiteSpace: 'nowrap' }}
                    />
                  </DataTable>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      <section className="grid rtu-stable-layout">
        <div className="col-12" style={{ padding: '0.25rem' }}>
          <RTUDiagnosticsTabbedTree rtuDiagnostics={data?.rtuDiagnostics} />
        </div>
      </section>
    </>
  );
};

export default LegacyV2RtuDetailView;
